import { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";
import { canWrite } from "@/lib/rbac";
import { toast } from "sonner";
import {
  AlertTriangle,
  RefreshCw,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

type ResolvedFilter = "ALL" | "OPEN" | "RESOLVED";
type SeverityFilter = "ALL" | "LOW" | "MEDIUM" | "HIGH";

const CATEGORY_LABELS: Record<string, string> = {
  NO_SHOW: "No-show",
  PRICE_GOUGING: "Prix abusif",
  QUALITY: "Qualité",
  BEHAVIOR: "Comportement",
  SPAM: "Spam",
  OTHER: "Autre",
};

const SEVERITY_VARIANT: Record<
  string,
  { variant: "default" | "secondary" | "destructive" | "outline"; label: string }
> = {
  LOW: { variant: "outline", label: "Faible" },
  MEDIUM: { variant: "secondary", label: "Moyenne" },
  HIGH: { variant: "destructive", label: "Élevée" },
};

function formatDate(d: string | Date | null | undefined) {
  if (!d) return "—";
  return new Date(d).toLocaleString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function OpsQuickReports() {
  const { user } = useAuth();
  const hasWrite = canWrite(user?.staffRole || null);

  const [resolvedFilter, setResolvedFilter] = useState<ResolvedFilter>("OPEN");
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>("ALL");
  const [page, setPage] = useState(1);
  const limit = 20;

  // Resolve dialog state
  const [resolveReportId, setResolveReportId] = useState<string | null>(null);
  const [resolveNote, setResolveNote] = useState("");

  const { data, isLoading, isError, refetch } = (trpc.admin as any).opsLeadOps.listQuickReports.useQuery({
    resolved:
      resolvedFilter === "ALL"
        ? undefined
        : resolvedFilter === "RESOLVED"
        ? true
        : false,
    severity: severityFilter === "ALL" ? undefined : severityFilter,
    page,
    limit,
  });

  const items: any[] = data?.items ?? [];
  const total: number = data?.total ?? 0;
  const totalPages: number = data?.totalPages ?? 1;

  const resolveMutation = (trpc.admin as any).opsLeadOps.resolveQuickReport.useMutation({
    onSuccess: () => {
      toast.success("Signalement résolu");
      setResolveReportId(null);
      setResolveNote("");
      refetch();
    },
    onError: (err: any) => toast.error(err?.message ?? "Erreur"),
  });

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <AlertTriangle className="h-6 w-6 text-warning" />
              Signalements OPS
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Quick reports créés par le staff sur les projets et artisans.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Rafraîchir
          </Button>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <CardTitle className="text-base">
                {total} signalement{total > 1 ? "s" : ""}
              </CardTitle>
              <div className="flex items-center gap-2">
                <Select
                  value={resolvedFilter}
                  onValueChange={(v) => {
                    setResolvedFilter(v as ResolvedFilter);
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-36 h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">Tous</SelectItem>
                    <SelectItem value="OPEN">Ouverts</SelectItem>
                    <SelectItem value="RESOLVED">Résolus</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={severityFilter}
                  onValueChange={(v) => {
                    setSeverityFilter(v as SeverityFilter);
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-36 h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">Toute sévérité</SelectItem>
                    <SelectItem value="HIGH">Élevée</SelectItem>
                    <SelectItem value="MEDIUM">Moyenne</SelectItem>
                    <SelectItem value="LOW">Faible</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-sm text-muted-foreground py-8 text-center">Chargement…</div>
            ) : isError ? (
              <div className="text-sm text-destructive py-8 text-center">Erreur de chargement</div>
            ) : items.length === 0 ? (
              <div className="text-sm text-muted-foreground py-8 text-center">
                Aucun signalement.
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Projet</TableHead>
                      <TableHead>Artisan</TableHead>
                      <TableHead>Catégorie</TableHead>
                      <TableHead>Sévérité</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Statut</TableHead>
                      {hasWrite && <TableHead className="text-right">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((r) => {
                      const sev = SEVERITY_VARIANT[r.severity] ?? {
                        variant: "outline" as const,
                        label: r.severity,
                      };
                      return (
                        <TableRow key={r.id}>
                          <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                            {formatDate(r.createdAt)}
                          </TableCell>
                          <TableCell className="text-sm">
                            <div className="font-medium">{r.project?.title ?? "—"}</div>
                            <div className="text-xs text-muted-foreground">
                              {r.project?.city ?? ""}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">
                            {r.company?.name ?? <span className="text-muted-foreground">—</span>}
                          </TableCell>
                          <TableCell className="text-sm">
                            {CATEGORY_LABELS[r.category] ?? r.category}
                          </TableCell>
                          <TableCell>
                            <Badge variant={sev.variant}>{sev.label}</Badge>
                          </TableCell>
                          <TableCell className="text-xs max-w-xs">
                            <div className="line-clamp-3">{r.description}</div>
                          </TableCell>
                          <TableCell>
                            {r.resolved ? (
                              <Badge variant="outline" className="text-success border-success/40">
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                Résolu
                              </Badge>
                            ) : (
                              <Badge variant="secondary">Ouvert</Badge>
                            )}
                          </TableCell>
                          {hasWrite && (
                            <TableCell className="text-right">
                              {!r.resolved && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setResolveReportId(r.id);
                                    setResolveNote("");
                                  }}
                                >
                                  Résoudre
                                </Button>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>

                {totalPages > 1 && (
                  <div className="flex items-center justify-between pt-3">
                    <div className="text-xs text-muted-foreground">
                      Page {page} / {totalPages}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={page <= 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={page >= totalPages}
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Resolve dialog */}
      <Dialog
        open={!!resolveReportId}
        onOpenChange={(open) => {
          if (!open) {
            setResolveReportId(null);
            setResolveNote("");
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Résoudre le signalement</DialogTitle>
            <DialogDescription>
              Marque le signalement comme résolu. Note optionnelle pour l'audit log.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="resolve-note">Note (optionnel)</Label>
            <Textarea
              id="resolve-note"
              value={resolveNote}
              onChange={(e) => setResolveNote(e.target.value)}
              rows={4}
              placeholder="Action prise, contexte, etc."
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setResolveReportId(null);
                setResolveNote("");
              }}
              disabled={resolveMutation.isLoading}
            >
              Annuler
            </Button>
            <Button
              onClick={() => {
                if (!resolveReportId) return;
                resolveMutation.mutate({
                  reportId: resolveReportId,
                  note: resolveNote.trim() || undefined,
                });
              }}
              disabled={resolveMutation.isLoading}
            >
              {resolveMutation.isLoading ? "En cours…" : "Confirmer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
